import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDoExamUser, submitExamUser } from '../../redux/apiRequest';
import { createAxios } from '../../createInstance';
import { loginSuccess } from '../../redux/authSlice';
import { useParams, useNavigate } from 'react-router-dom';
import { showToast } from "../../Components/ToastMessage";
import styles from './UserDoExam.module.scss';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

function UserDoExam() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.login?.currentUser);
    const axiosJWT = useMemo(() => createAxios(user, dispatch, loginSuccess), [user, dispatch]);

    const examState = useSelector((state) => state.exams.doexams);
    const [answers, setAnswers] = useState({}); // { questionId: selectedOptionNumber (1..4) }
    const [timeLeft, setTimeLeft] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const examData = examState?.data || null;

    const QuestionList = useMemo(() => {
        if (!Array.isArray(examState?.data?.user_exam?.exam?.questions)) return [];
        return examState.data.user_exam.exam.questions;
    }, [examState]);

    // Khởi tạo answers từ dữ liệu server (nếu user đã chọn trước đó)
    useEffect(() => {
        if (!QuestionList || QuestionList.length === 0) return;
        const init = {};
        QuestionList.forEach((q) => {
            // q.selected_option có thể là number (1..4) hoặc null
            if (typeof q.selected_option === 'number') {
                init[String(q.question_id)] = q.selected_option;
            }
        });
        if (Object.keys(init).length > 0) {
            setAnswers(init);
        }
    }, [QuestionList]);

    // Cảnh báo khi reload/thoát trang
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "Những thay đổi sẽ không được lưu và bài thi này sẽ được nộp.";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);

    // Fetch exam data
    useEffect(() => {
        if (user?.accessToken) {
            getDoExamUser(user.accessToken, dispatch, axiosJWT, user._id, id);
        }
    }, [user?.accessToken, dispatch, axiosJWT, id]);

    // Setup countdown từ doexam_at
    useEffect(() => {
        if (examData?.duration_seconds && examData?.user_exam?.doexam_at) {
            const startTime = new Date(examData.user_exam.doexam_at).getTime();
            const now = Date.now();
            const elapsed = Math.floor((now - startTime) / 1000);
            const remaining = examData.duration_seconds - elapsed;
            setTimeLeft(remaining > 0 ? remaining : 0);
        }
    }, [examData]);

    // Giảm dần countdown
    useEffect(() => {
        if (timeLeft === null) return;
        if (timeLeft <= 0) {
            // tự động nộp khi hết giờ (chỉ 1 lần)
            handleSubmit();
            return;
        }

        const timer = setInterval(() => {
            setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeLeft]);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const handleSelectOption = (qId, optionNumber) => {
        // đảm bảo lưu là number và key là string
        setAnswers((prev) => ({
            ...prev,
            [String(qId)]: Number(optionNumber),
        }));
    };

    const handleSubmit = async () => {
        if (!examData) return;
        if (isSubmitting) return;
        setIsSubmitting(true);

        // Gửi theo dạng object mapping để match server: { answers: { questionId: selectedOptionNumber, ... } }
        const payload = {
            answers: answers,
        };

        await submitExamUser(
            user.accessToken,
            dispatch,
            axiosJWT,
            user._id,
            id,
            payload,
            () => {
                showToast({
                    type: "success",
                    title: "Thành công!",
                    message: "Nộp bài thi thành công.",
                    duration: 3000
                });
                // navigate away
                navigate("/");
            },
            () => {
                showToast({
                    type: "error",
                    title: "Thất bại!",
                    message: "Nộp bài thi thất bại.",
                    duration: 3000
                });
                setIsSubmitting(false);
            }
        );
    };

    if (!examData) {
        return <p className={cx("loading")}>Đang tải dữ liệu bài thi...</p>;
    }

    return (
        <section className={cx("do-exam")}>
            {/* Header */}
            <header className={cx("exam-header")}>
                <h2>{examData.title}</h2>
                <div className={cx("exam-meta")}>
                    <span>Thời gian: {Math.floor(examData.duration_seconds / 60)} phút</span>
                    <span className={cx("countdown")}>⏳ {formatTime(timeLeft || 0)}</span>
                </div>
            </header>

            {/* Question list */}
            <div className={cx("questions")}>
                {QuestionList.map((q, index) => {
                    const qIdStr = String(q.question_id);
                    return (
                        <div key={qIdStr} className={cx("question-card")}>
                            <h4>Câu {index + 1}: {q.question_text}</h4>

                            {q.image_filename && (
                                <div className={cx("question-image")}>
                                    <img
                                        src={q.image_filename}
                                        alt={`Question ${index + 1}`}
                                    />
                                </div>
                            )}

                            <ul>
                                {q.options.map((opt, idx) => {
                                    const optionNumber = idx + 1; // 1..4
                                    return (
                                        <li key={idx}>
                                            <label>
                                                <input
                                                    type="radio"
                                                    name={`q-${qIdStr}`}
                                                    value={optionNumber}
                                                    checked={answers[qIdStr] === optionNumber}
                                                    onChange={() => handleSelectOption(qIdStr, optionNumber)}
                                                    disabled={examData.user_exam?.has_submitted}
                                                />
                                                {opt}
                                            </label>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    );
                })}
            </div>

            {/* Footer */}
            <footer className={cx("exam-footer")}>
                <button onClick={handleSubmit} disabled={isSubmitting || examData.user_exam?.has_submitted}>
                    {isSubmitting ? "Đang nộp..." : "Nộp bài"}
                </button>
            </footer>
        </section>
    );
}

export default UserDoExam;
